import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import { AppDispatch } from 'store'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { Button } from 'components/ui/Button/Button'
import { CursorPointer, Flex } from 'styling/GlobalStyles'
import { TitleText } from 'styling/Typography'
import useModalClose from 'hooks/useModalClose'
import useFocusFirstInput from 'utils/useFocusFirstInput'
import { IOpsDepartment } from 'types/opsDepartments.interface'
import { IOpsRaiseRequestForm } from 'types/opsToday.interface'
import { OpsRequestPriority } from 'types/opsRequests.interface'
import { raiseRequest } from 'features/operations/opsRequestsSlice'
import { deptName, opsDepartments } from '../Shared/opsDepartments'
import { REQUEST_PRIORITY_META } from '../Shared/opsRequestsData'

const PRIORITY_KEYS: OpsRequestPriority[] = ['low', 'normal', 'high', 'critical']

const PRIORITY_OPTIONS = PRIORITY_KEYS.map((p) => ({
  label: REQUEST_PRIORITY_META[p].label,
  value: p,
}))

const TIME_PATTERN = /^\d{2}:\d{2}$/

export const RaiseRequestModal = ({
  dept,
  closeModal,
}: {
  dept: IOpsDepartment
  closeModal: () => void
}) => {
  const [state, setState] = useState<IOpsRaiseRequestForm>({
    toDept: null,
    title: '',
    description: '',
    priority: 'normal',
    neededBy: '',
  })
  const dispatch = useDispatch<AppDispatch>()
  useFocusFirstInput()
  useModalClose(closeModal)

  const deptOptions = opsDepartments()
    .filter((d) => d.key !== dept.key)
    .map((d) => ({ label: d.name, value: d.key }))

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, name: string) => {
    setState({ ...state, [name]: e.target.value })
  }

  const timeValid = TIME_PATTERN.test(state.neededBy)
  const isValid =
    Boolean(state.toDept) && Boolean(state.title.trim()) && Boolean(state.description.trim()) && timeValid

  const handleSave = () => {
    if (!state.toDept) return
    dispatch(
      raiseRequest({
        fromDept: dept.key,
        toDept: state.toDept,
        title: state.title.trim(),
        description: state.description.trim(),
        linkedEntityLabel: null,
        linkedPath: null,
        priority: state.priority,
        neededBy: state.neededBy,
        createdBy: `You (${dept.name})`,
      }),
    )
    toast.success(`REQ sent to ${deptName(state.toDept)} — they see it in their queue`)
    closeModal()
  }

  return (
    <Modal black>
      <Flex column between center>
        <Flex column gap='1rem' width='100%'>
          <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
            <TitleText weight='bold'>Raise request</TitleText>
            <CursorPointer onClick={closeModal}>
              <CloseIcon />
            </CursorPointer>
          </Flex>
          <HonestSelectInput
            nomarg
            labelText='To department'
            name='toDept'
            placeholder='Who do you need?'
            options={deptOptions}
            required
            invalid={!state.toDept}
            onChange={(v: any) => setState((prev) => ({ ...prev, toDept: v?.value || null }))}
          />
          <Input
            nomarg
            labelText='Title'
            placeholder='What do you need — one line'
            value={state.title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, 'title')}
            required
            invalid={!state.title.trim()}
          />
          <Input
            nomarg
            type='textarea'
            height='6rem'
            labelText='Description'
            placeholder='Give them enough detail to act without calling you back...'
            value={state.description}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, 'description')}
            required
            invalid={!state.description.trim()}
          />
          <HonestSelectInput
            nomarg
            labelText='Priority'
            name='priority'
            options={PRIORITY_OPTIONS}
            defaultValue='normal'
            onChange={(v: any) =>
              setState((prev) => ({ ...prev, priority: (v?.value || 'normal') as OpsRequestPriority }))
            }
          />
          <Input
            nomarg
            labelText='Needed by'
            placeholder='HH:mm'
            value={state.neededBy}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, 'neededBy')}
            required
            invalid={!timeValid}
            customInvalidMessage='Use HH:mm — for example 09:30'
          />
        </Flex>
        <Button style={{ marginTop: '16px' }} disabled={!isValid} variant='primary' onClick={handleSave}>
          Save
        </Button>
      </Flex>
    </Modal>
  )
}
